import React, { useState, useEffect, useCallback, useRef } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { privateApi } from "../../api/axiosConfig";
import {
  IRANIAN_MOBILE_LENGTH,
  isCompleteMobile,
  isValidIranianMobile,
  normalizeMobileInput,
  INVALID_MOBILE_MESSAGE,
} from "../../utils/phone";

const REDIRECT_DELAY_SECONDS = 5;

function translateInvoiceBackendMessage(raw, fallback) {
  const defaultMessage = fallback || "تأیید فاکتور با خطا مواجه شد.";
  if (!raw) return defaultMessage;

  const text = String(raw);
  if (/[؀-ۿ]/.test(text)) return text;

  const lower = text.toLowerCase();
  if (lower.includes("insufficient available stock"))
    return "موجودی انبار برای این تعداد کافی نیست.";
  if (lower.includes("insufficient stock"))
    return "موجودی انبار برای این تعداد کافی نیست.";
  if (lower.includes("out of stock")) return "موجودی این کالا تمام شده است.";
  if (
    lower.includes("stock") ||
    lower.includes("inventory") ||
    lower.includes("موجودی")
  )
    return "تعداد درخواستی بیشتر از موجودی انبار است.";

  return defaultMessage;
}

function extractInvoiceErrorMessage(err, fallback) {
  const defaultMessage = fallback || "تأیید فاکتور با خطا مواجه شد.";
  if (err.response?.status === 401) {
    return "لطفا برای انجام این عملیات ابتدا وارد حساب کاربری خود شوید.";
  }
  if (err.response && err.response.data) {
    const data = err.response.data;
    if (data.detail)
      return translateInvoiceBackendMessage(data.detail, defaultMessage);
    if (data.error)
      return translateInvoiceBackendMessage(data.error, defaultMessage);
    if (data.message)
      return translateInvoiceBackendMessage(data.message, defaultMessage);
    if (data.non_field_errors)
      return translateInvoiceBackendMessage(
        Array.isArray(data.non_field_errors)
          ? data.non_field_errors[0]
          : data.non_field_errors,
        defaultMessage,
      );

    const firstKey = Object.keys(data)[0];
    if (firstKey && Array.isArray(data[firstKey])) {
      return translateInvoiceBackendMessage(data[firstKey][0], defaultMessage);
    }

    if (typeof data === "string")
      return translateInvoiceBackendMessage(data, defaultMessage);
  }
  return defaultMessage;
}

function DetailRow({ label, value }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
    >
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={500}
        textAlign="left"
        sx={{
          overflowWrap: "anywhere",
          minWidth: 0,
        }}
      >
        {value ?? "—"}
      </Typography>
    </Stack>
  );
}

function InvoiceDetails({
  invoiceId,
  redirectOnAccept = true,
  redirectTo = "/invoices",
  redirectDelay = REDIRECT_DELAY_SECONDS,
}) {
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [redirectCountdown, setRedirectCountdown] = useState(null);

  const successRef = useRef(null);
  const messageAnchorRef = useRef(null);
  const redirectTimerRef = useRef(null);

  const clearRedirectTimer = () => {
    if (redirectTimerRef.current) {
      clearInterval(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearRedirectTimer();
    };
  }, []);

  useEffect(() => {
    if (redirectCountdown === null) return;

    if (redirectCountdown <= 0) {
      clearRedirectTimer();
      navigate(redirectTo);
      return;
    }

    redirectTimerRef.current = setInterval(() => {
      setRedirectCountdown((current) =>
        current === null ? null : Math.max(0, current - 1),
      );
    }, 1000);

    return () => {
      clearRedirectTimer();
    };
  }, [redirectCountdown, navigate, redirectTo]);

  const [shipping, setShipping] = useState({
    recipient_name: "",
    recipient_phone: "",
    province: "",
    city: "",
    shipping_address: "",
    delivery_notes: "",
  });

  const fetchInvoice = useCallback(async () => {
    if (invoiceId === null || invoiceId === undefined || invoiceId === "") {
      setError("شناسه فاکتور مشخص نشده است.");
      setInvoice(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    setRedirectCountdown(null);
    clearRedirectTimer();

    try {
      const url = `/api/v1/checkout/invoice/${invoiceId}/`;

      const response = await privateApi.get(url);

      setInvoice(response.data);

      setShipping({
        recipient_name: response.data.recipient_name || "",
        // A value stored before the phone field was constrained can still hold
        // Persian digits or a +98 prefix; it never passes through
        // handleShippingChange, so normalize it on the way in.
        recipient_phone: normalizeMobileInput(response.data.recipient_phone),
        province: response.data.province || "",
        city: response.data.city || "",
        shipping_address: response.data.shipping_address || "",
        delivery_notes: response.data.delivery_notes || "",
      });
    } catch (err) {
      setError(
        extractInvoiceErrorMessage(
          err,
          `دریافت فاکتور ناموفق بود. ${
            err.response?.status
              ? `کد خطا: ${err.response.status}`
              : err.message
          }`,
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  // Smooth-scroll to the feedback message whenever success/error appears.
  // Uses an anchor wrapper (always mounted after invoice loads) + timeout
  // so scrolling runs after React has painted the Alert.
  useEffect(() => {
    if ((!success && !error) || !messageAnchorRef.current) return;

    const timer = setTimeout(() => {
      const target = successRef.current || messageAnchorRef.current;
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);

    return () => clearTimeout(timer);
  }, [success, error]);

  const handleShippingChange = (event) => {
    const { name, value } = event.target;

    setShipping((current) => ({
      ...current,
      // Keeps the field phone-shaped: Latin digits only, international prefixes
      // rewritten, hard-capped at the national length. Values arriving from the
      // server (InvoiceDetails.jsx:184-191) bypass this, so re-check them there.
      [name]: name === "recipient_phone" ? normalizeMobileInput(value) : value,
    }));
  };

  const handleAccept = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    setRedirectCountdown(null);
    clearRedirectTimer();

    try {
      const response = await privateApi.post(
        `/api/v1/checkout/invoice/${invoiceId}/accept/`,
        shipping,
      );

      setInvoice(response.data);
      setSuccess(
        "اطلاعات ارسال ثبت شد و فاکتور تأیید شد. فرآیند با موفقیت تکمیل شد.",
      );
      if (redirectOnAccept) {
        setRedirectCountdown(redirectDelay);
      }
    } catch (err) {
      setError(
        extractInvoiceErrorMessage(err, "تأیید فاکتور با خطا مواجه شد."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm("آیا از رد کردن این فاکتور اطمینان دارید؟")) {
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");
    setRedirectCountdown(null);
    clearRedirectTimer();

    try {
      const response = await privateApi.post(
        `/api/v1/checkout/invoice/${invoiceId}/reject/`,
      );

      if (response.data && typeof response.data === "object") {
        setInvoice((current) => ({
          ...current,
          ...response.data,
        }));
      } else {
        setInvoice((current) => ({
          ...current,
          status: "rejected",
        }));
      }

      setSuccess("فاکتور با موفقیت رد شد.");
    } catch (err) {
      setError(extractInvoiceErrorMessage(err, "رد فاکتور با خطا مواجه شد."));
    } finally {
      setSubmitting(false);
    }
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }

    const number = Number(String(value).replace(/,/g, ""));

    return Number.isFinite(number)
      ? new Intl.NumberFormat("fa-IR").format(number)
      : String(value);
  };

  const formatInvoiceDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    return Number.isNaN(date.getTime())
      ? "—"
      : new Intl.DateTimeFormat("fa-IR", {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(date);
  };

  const invoiceStatuses = {
    pending: { label: "در انتظار تأیید", color: "warning" },
    approved: { label: "تأیید شده", color: "success" },
    accepted: { label: "تأیید شده", color: "success" },
    rejected: { label: "رد شده", color: "error" },
    paid: { label: "پرداخت شده", color: "success" },
    cancelled: { label: "لغو شده", color: "default" },
    expired: { label: "منقضی شده", color: "default" },
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Alert severity="error">
        {error || "اطلاعات فاکتور در دسترس نیست."}
        <Button onClick={fetchInvoice} size="small">
          تلاش مجدد
        </Button>
      </Alert>
    );
  }

  const status = invoiceStatuses[invoice.status] || {
    label: invoice.status || "نامشخص",
    color: "default",
  };

  const canRespond = ["pending"].includes(invoice.status) && !submitting;

  return (
    <Box
      sx={{
        mt: 0,
        p: { xs: 1.5, sm: 2 },
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        mb={2}
      >
        <Typography variant="h6" fontWeight={700}>
          فاکتور شماره {formatMoney(invoice.id)}
        </Typography>

        <Chip label={status.label} color={status.color} size="small" />
      </Stack>

      <Box ref={messageAnchorRef} sx={{ scrollMarginTop: 96 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            role="status"
            aria-live="polite"
            tabIndex={-1}
            sx={{ mb: 2 }}
          >
            <Stack ref={successRef} spacing={1} sx={{ scrollMarginTop: 96 }}>
              <Typography variant="body2" fontWeight={600}>
                {success}
              </Typography>
              {redirectCountdown !== null && (
                <>
                  <Typography variant="body2">
                    تا {redirectCountdown.toLocaleString("fa-IR")} ثانیه دیگر به
                    صفحه فاکتورها منتقل می‌شوید تا پرداخت را ادامه دهید.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => {
                        clearRedirectTimer();
                        setRedirectCountdown(null);
                        navigate(redirectTo);
                      }}
                    >
                      انتقال به صفحه فاکتورها
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      color="inherit"
                      onClick={() => {
                        clearRedirectTimer();
                        setRedirectCountdown(null);
                      }}
                    >
                      ماندن در این صفحه
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          </Alert>
        )}
      </Box>

      <Stack spacing={1}>
        <DetailRow
          label="تاریخ ثبت فاکتور"
          value={formatInvoiceDate(invoice.created_at)}
        />

        <DetailRow
          label="مبلغ نهایی"
          value={`${formatMoney(invoice.total_amount)} تومان`}
        />
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Typography fontWeight={700} mb={1.5}>
        اقلام فاکتور
      </Typography>

      {Array.isArray(invoice.order_items) && invoice.order_items.length > 0 ? (
        <Stack spacing={1.5}>
          {invoice.order_items.map((item) => {
            const product =
              typeof item.product === "object" && item.product
                ? item.product
                : null;

            return (
              <Box
                key={item.id}
                sx={{
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 2,
                }}
              >
                {/* Product Info */}
                <Typography
                  component={Link}
                  to={
                    product?.product_slug
                      ? `/product/${product.product_slug}`
                      : undefined
                  }
                  fontWeight={600}
                  sx={{
                    color: "text.primary",
                    textDecoration: "none",
                    "&:hover": {
                      color: "primary.main",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {product?.product_title ||
                    (typeof item.product === "string"
                      ? item.product
                      : `کالای شماره ${item.id}`)}
                </Typography>

                <Stack spacing={0.5} mt={1}>
                  <DetailRow
                    label="تعداد بسته"
                    value={formatMoney(item.quantity)}
                  />

                  <DetailRow
                    label="مجموع واحدها"
                    value={formatMoney(item.total_units)}
                  />

                  <DetailRow
                    label="قیمت هر بسته"
                    value={`${formatMoney(item.price_at_purchase)} تومان`}
                  />

                  <DetailRow
                    label="جمع این کالا"
                    value={`${formatMoney(item.line_total)} تومان`}
                  />
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Alert severity="info">اقلام فاکتور موجود نیستند.</Alert>
      )}

      {canRespond && (
        <>
          <Divider sx={{ my: 2 }} />

          <Typography fontWeight={700} mb={1.5}>
            اطلاعات گیرنده و ارسال
          </Typography>

          {/* User Info  */}

          <Box
            component="form"
            onSubmit={handleAccept}
            dir="rtl"
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              border: 1,
              borderColor: "divider",
              borderRadius: 3,
              bgcolor: "background.default",

              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.paper",
                transition: "box-shadow 0.2s, border-color 0.2s",
                "&.Mui-focused": {
                  boxShadow: (theme) =>
                    `0 0 0 3px ${theme.palette.primary.main}18`,
                },
              },

              "& .MuiInputBase-input": {
                direction: "rtl",
                textAlign: "right",
              },

              "& .MuiInputBase-inputMultiline": {
                textAlign: "right",
              },
            }}
          >
            <Stack spacing={3}>
              {/* Recipient information */}
              <Box>
                <Typography fontWeight={700} variant="subtitle1" mb={0.5}>
                  اطلاعات گیرنده
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  مشخصات فردی که سفارش را تحویل می‌گیرد وارد کنید.
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    label="نام و نام خانوادگی گیرنده"
                    name="recipient_name"
                    value={shipping.recipient_name}
                    onChange={handleShippingChange}
                    required
                    fullWidth
                    autoComplete="name"
                    inputProps={{ dir: "rtl" }}
                  />

                  <TextField
                    label="شماره تلفن همراه"
                    name="recipient_phone"
                    value={shipping.recipient_phone}
                    onChange={handleShippingChange}
                    required
                    fullWidth
                    type="tel"
                    autoComplete="tel"
                    placeholder="مثال: 09123456789"
                    error={
                      isCompleteMobile(shipping.recipient_phone) &&
                      !isValidIranianMobile(shipping.recipient_phone)
                    }
                    helperText={
                      isCompleteMobile(shipping.recipient_phone) &&
                      !isValidIranianMobile(shipping.recipient_phone)
                        ? INVALID_MOBILE_MESSAGE
                        : " "
                    }
                    FormHelperTextProps={{ sx: { textAlign: "right" } }}
                    inputProps={{
                      dir: "ltr",
                      inputMode: "numeric",
                      maxLength: IRANIAN_MOBILE_LENGTH,
                      // direction ltr keeps the digits in order; text-align
                      // right matches the placeholder alignment of the rest of
                      // the Persian form.
                      style: { textAlign: "right", direction: "ltr" },
                    }}
                  />
                </Stack>
              </Box>

              <Divider />

              {/* Address information */}
              <Box>
                <Typography fontWeight={700} variant="subtitle1" mb={0.5}>
                  نشانی تحویل سفارش
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  استان، شهر و نشانی دقیق محل تحویل را مشخص کنید.
                </Typography>

                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="استان"
                      name="province"
                      value={shipping.province}
                      onChange={handleShippingChange}
                      required
                      fullWidth
                      autoComplete="address-level1"
                      inputProps={{ dir: "rtl" }}
                    />

                    <TextField
                      label="شهر"
                      name="city"
                      value={shipping.city}
                      onChange={handleShippingChange}
                      required
                      fullWidth
                      autoComplete="address-level2"
                      inputProps={{ dir: "rtl" }}
                    />
                  </Stack>

                  <TextField
                    label="آدرس کامل"
                    name="shipping_address"
                    value={shipping.shipping_address}
                    onChange={handleShippingChange}
                    required
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={6}
                    autoComplete="street-address"
                    placeholder="خیابان، کوچه، پلاک، واحد و سایر جزئیات..."
                    inputProps={{ dir: "rtl" }}
                  />
                </Stack>
              </Box>

              <Divider />

              {/* Optional delivery notes */}
              <Box>
                <Typography fontWeight={700} variant="subtitle1" mb={0.5}>
                  توضیحات ارسال
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mr: 1, fontWeight: 400 }}
                  >
                    (اختیاری)
                  </Typography>
                </Typography>

                <Typography variant="body2" color="text.secondary" mb={2}>
                  اطلاعات تکمیلی برای تحویل سفارش را در صورت نیاز بنویسید.
                </Typography>

                <TextField
                  label="توضیحات برای پیک یا شرکت حمل‌ونقل"
                  name="delivery_notes"
                  value={shipping.delivery_notes}
                  onChange={handleShippingChange}
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  placeholder="مثلاً زمان مناسب تحویل یا نشانه‌های محل..."
                  inputProps={{ dir: "rtl" }}
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    "& .MuiInputLabel-root": {
                      maxWidth: "calc(100% - 32px)",
                      whiteSpace: "normal",
                      textAlign: "right",
                    },
                    "& .MuiInputLabel-shrink": {
                      maxWidth: "calc(100% - 24px)",
                    },
                    "& .MuiOutlinedInput-root": {
                      boxSizing: "border-box",
                      minWidth: 0,
                    },
                    "& .MuiOutlinedInput-input": {
                      boxSizing: "border-box",
                      minWidth: 0,
                      overflowWrap: "anywhere",
                    },
                  }}
                />
              </Box>

              {/* Form actions */}
              <Divider />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={submitting}
                  fullWidth
                  sx={{
                    flex: 2,
                    minHeight: 48,
                    borderRadius: 2,
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { boxShadow: 1 },
                  }}
                >
                  {submitting ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} color="inherit" />
                      <span>در حال ثبت اطلاعات...</span>
                    </Stack>
                  ) : (
                    "تأیید  و ثبت آدرس"
                  )}
                </Button>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
              >
                پیش از تأیید، صحت اطلاعات گیرنده و نشانی تحویل را بررسی کنید.
              </Typography>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
}

export default InvoiceDetails;
